<template>
  <ConfirmDeleteModal
    :open="modal_config.delete_open"
    @close-modal="modal_config.delete_open = false"
    @refresh="dataStore.setData(['documents'])"
    :modal_config="modal_config"
  />
  <EditDocumentModal
    :open="modal_config.edit_open"
    @close-modal="modal_config.edit_open = false"
    @refresh="dataStore.setData(['documents'])"
    :modal_config="modal_config"
  />
  <PreviewDocumentModal
    :open="modal_config.preview_open"
    @close-modal="modal_config.preview_open = false"
    :modal_config="modal_config"
  />
  <UploadDocumentsModal
    :open="modal_config.upload_open"
    @close-modal="modal_config.upload_open = false"
    @refresh="dataStore.setData(['documents'])"
    :modal_config="modal_config"
  />

  <div class="mb-40">
    <BreadCrumbs
      class="mb-[1.625rem]"
      :crumbs="[
        {
          name: 'Documents',
          path: '/documents',
        },
      ]"
    />
    <div class="mb-6 md:mb-[2.125rem] flex justify-between items-center">
      <div class="">
        <div
          class="mb-[.375rem] text-grey-8 dark:text-grey-2 dark:text-opacity-40 text-opacity-40 font-league font-semibold text-base"
        >
          Documents
        </div>
        <div class="text-strongest font-league text-[2rem] font-semibold tracking-[-0.64px]">
          All Documents
        </div>
      </div>
      <div class="md:flex gap-2 hidden">
        <Button variant="paper-primary" @click="downloadTODForm()" class="hidden md:flex">
          <template #icon>
            <Icon name="Download" class="w-4 h-4" />
          </template>
          <template #label> Download TOD Form </template>
        </Button>
        <Button variant="paper-primary" @click="openUploadModal()" isIconOnlyOnMobile>
          <template #icon>
            <Icon name="Upload" class="w-4 h-4" />
          </template>
          <template #label> Upload </template>
        </Button>
      </div>
    </div>
    <!-- Component Body -->
    <div>
      <div class="w-full mb-5">
        <FilterPills
          :active-filters="applied_filters"
          @apply-filters="addFilter"
          @clear-filter="removeFilter"
          @clear-all-filters="clearFilters"
          :filters="base_filters"
        />
      </div>

      <Table
        :table_headers="table_headers"
        :table_data="table_data"
        variant="secondary"
        :has_actions="true"
        :fields_with_custom_styles="[]"
        mobile_primary_field="file_name"
        :hide_headers="false"
        :hide_scrollbar="false"
        empty_icon="File"
        empty_primary_text="No documents found"
        empty_secondary_text="Use the upload button to add documents."
        :column_gap_px="48"
      >
        <template v-slot:actions="entry">
          <div class="gap-1 flex">
            <Button variant="paper-primary" size="sm" @click="previewDocument(entry.row)">
              <template #icon>
                <Icon name="Eye" class="w-4 h-4" />
              </template>
            </Button>
            <Button
              variant="paper-primary"
              class="items-center flex"
              size="sm"
              @click="editDocument(entry.row)"
            >
              <template #icon>
                <Icon name="Pencil" class="w-4 h-4" />
              </template>
            </Button>
            <Button variant="paper-primary" size="sm" @click="downloadDocument(entry.row)">
              <template #icon>
                <Icon name="Download" class="w-4 h-4" />
              </template>
            </Button>
            <Button
              variant="paper-primary"
              class="items-center flex"
              size="sm"
              @click="deleteDocument(entry.row)"
            >
              <template #icon>
                <Icon name="Trash2" class="w-4 h-4 stroke-error" />
              </template>
            </Button>
          </div>
        </template>
      </Table>
    </div>
  </div>
  <div
    class="fixed inset-x-0 bottom-0 rounded-t-[20px] z-20 bg-purchase-flow-bottom-nav-gradient btn-bg-swap md:hidden p-4 flex items-center justify-center gap-2"
  >
    <Button variant="paper-primary" @click="downloadTODForm()">
      <template #icon>
        <Icon name="Download" class="w-4 h-4" />
      </template>
      <template #label> Download TOD Form </template>
    </Button>
    <Button variant="paper-primary" @click="openUploadModal()">
      <template #icon>
        <Icon name="Upload" class="w-4 h-4" />
      </template>
      <template #label> Upload Documents</template>
    </Button>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { BreadCrumbs, Button, Icon, FilterPills, Table } from "@/components";
import { useSchemaStore, useDataStore, useCrudStore } from "@/stores";
import {
  ConfirmDeleteModal,
  PreviewDocumentModal,
  EditDocumentModal,
  UploadDocumentsModal,
} from "../InvestorDocuments";
import useFileUtility from "@/composables/useFileUtility";

const { downloadFile_R2, fetchFileForPreview_R2, deleteFile_R2 } = useFileUtility();

const schemaStore = useSchemaStore();
const dataStore = useDataStore();
const crudStore = useCrudStore();

const base_filters = computed(() => {
  let file_category_schema_record = schemaStore.all_document_schema.find(
    (field) => field.field_name === "file_category"
  );

  let file_category_options = [];

  if (file_category_schema_record?.record_detail_config?.default?.dropdown_options) {
    file_category_options =
      file_category_schema_record.record_detail_config.default.dropdown_options.map((opt) => ({
        label: opt,
        value: opt,
      }));
    file_category_options = file_category_options.filter((opt) => opt.value !== "Other");
  }

  let filters = [
    {
      filter_type: "date-range",
      table: "documents",
      label: "Created Date",
      field_name: "created_date",
    },
    {
      filter_type: "picklist",
      table: "documents",
      label: "File Category",
      field_name: "file_category",
      options: file_category_options,
    },
  ];
  return filters;
});
const table_headers = computed(() => {
  let headers = schemaStore.getTableHeadersForCollection("documents");

  // find the header where field name is file_type and change it to formatted file type
  let file_type_header = headers.find((header) => header.field_name === "file_type");
  if (file_type_header) {
    file_type_header.field_name = "formatted_file_type";
    file_type_header.label = "File Type";
  }

  return headers;
});

const table_data = computed(() => {
  const docs = dataStore.documents ?? [];
  const filters = applied_filters.value ?? [];
  if (!filters.length) return docs;

  // Collect date range (if multiple date filters exist, we AND them by intersecting ranges)
  let start = null;
  let end = null;

  // Collect all selected categories
  const categories = [];

  for (const f of filters) {
    if (!f?.field_name) continue;

    if (f.field_name === "created_date") {
      const s = f.start ? new Date(f.start) : null;
      const e = f.end ? new Date(f.end) : null;

      if (s && !isNaN(s)) start = start ? new Date(Math.max(start.getTime(), s.getTime())) : s;
      if (e && !isNaN(e)) end = end ? new Date(Math.min(end.getTime(), e.getTime())) : e;
    }

    if (f.field_name === "file_category" && f.value != null && f.value !== "") {
      categories.push(f.value);
    }
  }

  return docs.filter((doc) => {
    // date AND
    if (start || end) {
      const created = doc?.created_date ? new Date(doc.created_date) : null;
      if (!created || isNaN(created)) return false;
      if (start && created < start) return false;
      if (end && created > end) return false;
    }

    // categories (single string) => OR
    if (categories.length) {
      return categories.includes(doc?.file_category);
    }

    return true;
  });
});

const applied_filters = ref([]);

let modals_open = {
  upload_open: false,
  preview_open: false,
  edit_open: false,
  delete_open: false,
};

const modal_config = ref({
  ...modals_open,
});

function openUploadModal() {
  let options = [];
  let file_category_field = schemaStore.all_document_schema.find(
    (field) => field.field_name === "file_category"
  );
  if (file_category_field?.record_detail_config?.default?.dropdown_options) {
    options = file_category_field.record_detail_config.default.dropdown_options;
  }

  modal_config.value = {
    ...modals_open,
    upload_open: true,
    file_category_options: options,
  };
}
async function downloadTODForm() {
  let r2_key = `${import.meta.env.VITE_TOD_BEN_FORM_R2_KEY}`;

  let file = null;
  // if r2_key ends with .pdf
  if (r2_key.endsWith(".pdf")) {
    file = {
      r2_key: r2_key,
      file_name: "TOD_Beneficiary_Form.pdf",
      file_type: "application/pdf",
      is_base64: false, // or false
    };
  } else if (r2_key.endsWith(".docx")) {
    file = {
      r2_key: r2_key,
      file_name: "TOD_Beneficiary_Form.docx",
      file_type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      is_base64: false, // or false
    };
  }
  if (file) {
    await downloadFile_R2(file);
  }
}
function addFilter(filteringEvent) {
  let { field_name, filters } = filteringEvent;
  applied_filters.value = applied_filters.value.filter((f) => f.field_name !== field_name);
  applied_filters.value.push(...filters);
}
function removeFilter(filter) {
  applied_filters.value = applied_filters.value.filter(
    (f) => !(f.field_name === filter.field_name && f.value === filter.value)
  );
}
function clearFilters() {
  applied_filters.value = [];
}
async function previewDocument(doc) {
  let file_url = await fetchFileForPreview_R2(doc);

  modal_config.value = {
    ...modals_open,
    preview_open: true,
    record: doc,
    file_url: file_url,
  };
}
function editDocument(doc) {
  let document = JSON.parse(JSON.stringify(doc));
  let og_document = JSON.parse(JSON.stringify(doc));
  let options = [];
  let file_category_field = schemaStore.all_document_schema.find(
    (field) => field.field_name === "file_category"
  );
  if (file_category_field?.record_detail_config?.default?.dropdown_options) {
    options = file_category_field.record_detail_config.default.dropdown_options;
  }

  modal_config.value = {
    ...modals_open,
    edit_open: true,
    record: document,
    og_record: og_document,
    file_category_options: options,
  };
}
function downloadDocument(doc) {
  downloadFile_R2(doc);
}
async function deleteDocument(doc) {
  modal_config.value = {
    ...modals_open,
    delete_open: true,
    record: doc,
  };
}
</script>
